import React__default, { forwardRef, useState, useRef, useId, useCallback, useEffect, useImperativeHandle } from 'react';
import classnames from 'classnames';
import styles from './TextField.module.css.js';
import IconEye from './IconEye.svg.js';

/* eslint-disable react/display-name */
const TextField = forwardRef(({ label, name, placeholder, type, required, disabled, passwordToggleVisibility, value, hint, error, icon, ...props }, ref) => {
    const [focus, setFocus] = useState(value ? true : false);
    const [active, setActive] = useState(value ? true : false);
    const [autoFill, setAutofill] = useState(false);
    const [passwordHidden, setPasswordHidden] = useState(true);
    const [inputError, setInputError] = useState(error);
    const inputBoxRef = useRef(null);
    const inputRef = useRef(null);
    const id = useId();
    const handleClickOutside = useCallback((event) => {
        if (focus === false) {
            inputRef.current.blur();
            return;
        }
        if (event.type === "blur" && inputRef.current.value === "") {
            setFocus(false);
            setActive(false);
        }
        if (event instanceof MouseEvent &&
            event.target instanceof HTMLElement &&
            event.target.contains(inputBoxRef.current)) {
            if (inputRef.current.value === "")
                setFocus(false);
            setActive(false);
        }
    }, [focus, inputRef]);
    const handleFocus = () => {
        setFocus(true);
        setActive(true);
    };
    const handleTyping = () => {
        // clear possible errors
        setInputError(false);
    };
    const togglePassword = () => {
        if (inputRef.current.type === "password") {
            inputRef.current.type = "text";
            setPasswordHidden(false);
        }
        else {
            inputRef.current.type = "password";
            setPasswordHidden(true);
        }
    };
    useEffect(() => {
        if (focus)
            inputRef.current.focus();
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [focus, handleClickOutside]);
    useEffect(() => {
        setInputError(error);
    }, [error]);
    useEffect(() => {
        const input = inputRef.current;
        const onAnimationStart = (e) => {
            if (e.animationName === styles.onAutoFillStart) {
                setAutofill(true);
            }
        };
        input.addEventListener("animationstart", onAnimationStart);
        return () => {
            input.removeEventListener("animationstart", onAnimationStart);
        };
    }, []);
    useImperativeHandle(ref, () => ({
        clearValue: () => {
            inputRef.current.value = "";
            setFocus(false);
        },
    }));
    const TextFieldStyles = classnames(styles.TextField, {
        [styles.TextField___focus]: focus || autoFill,
        [styles.TextField___active]: active,
        [styles.TextField___error]: inputError,
        [styles.TextField___disabled]: disabled,
    });
    const fieldProps = {
        className: styles.TextField__input,
        placeholder: placeholder
            ? placeholder.charAt(0).toUpperCase() + placeholder.slice(1)
            : "",
        name,
        defaultValue: value,
        required,
        disabled,
        type,
        onClick: handleFocus,
        onFocus: handleFocus,
        onBlur: handleClickOutside,
        onKeyDown: handleTyping,
        ...props,
    };
    const TogglePasswordStyles = classnames(styles.TextField__TogglePassword, {
        [styles.TextField__TogglePassword___slash]: passwordHidden,
    });
    const renderIcon = () => {
        if (type === "password" && passwordToggleVisibility) {
            return (React__default.createElement("span", { className: TogglePasswordStyles, onClick: togglePassword, "aria-hidden": true },
                React__default.createElement(IconEye, null)));
        }
        else if (icon) {
            return (React__default.createElement("span", { className: styles.TextField__icon, "aria-hidden": true }, icon));
        }
    };
    return (React__default.createElement("div", { className: TextFieldStyles },
        React__default.createElement("label", { htmlFor: id },
            React__default.createElement("span", { className: styles.TextField__label },
                label,
                required ? " *" : ""),
            React__default.createElement("div", { className: styles.TextField__inputWrapper, ref: inputBoxRef },
                React__default.createElement("input", { id: id, ref: inputRef, ...fieldProps }),
                renderIcon())),
        hint && React__default.createElement("p", { className: styles.TextField__hint }, hint)));
});
TextField.displayName = "TextField";

export { TextField };
//# sourceMappingURL=TextField.js.map
